import env, { EEnvs } from 'constants/env';

import AlertsService from 'services/AlertsService';

export const ReducedCookieStorage = {
    setItem: (key: string, value: string): string => {
        const isHttp = window.location.protocol === 'http:';
        if (ReducedCookieStorage.shouldUseLocalStorage(key)) {
            try {
                localStorage.setItem(key, value);
            } catch (error) {}
        } else {
            document.cookie = [
                `${key}=${value}`,
                `max-age=${60 * 60 * 24 * 365}`,
                `domain=${cookieDomain}`,
                `path=/`,
                isHttp ? `` : `secure;`,
                `SameSite=strict`,
            ].join(';');
        }

        if (key.endsWith('refreshToken')) {
            ReducedCookieStorage.setItem('refreshTokenDate', '' + Date.now());
        }

        return value;
    },

    getItem: (key: string): string => {
        if (ReducedCookieStorage.shouldUseLocalStorage(key)) {
            return localStorage.getItem(key) || '';
        } else {
            const cookies = document.cookie.split(';');
            let item = '';
            cookies.forEach((cookie) => {
                const name = cookie.trim().split('=')[0];
                if (name === key) {
                    item = cookie.trim().split('=')[1];
                }
            });
            return item;
        }
    },

    removeItem: (key: string): void => {
        if (ReducedCookieStorage.shouldUseLocalStorage(key)) {
            localStorage.removeItem(key);
        } else {
            document.cookie = `${key}=1; expires=Thu, 01-Jan-1970 00:00:01 GMT; domain=${cookieDomain}; path=/`;
        }

        if (key.endsWith('refreshToken')) {
            ReducedCookieStorage.removeItem('refreshTokenDate');
        }
    },

    shouldUseLocalStorage: (key: string): boolean => {
        return (
            isLocalStorageAvailable &&
            (key.endsWith('accessToken') || key.endsWith('userData') || key.endsWith('clockDrift'))
        );
    },

    clear: (): void => {},

    sync: () => {
        return Promise.resolve();
    },
};

const cookieDomain =
    process.env.NODE_ENV === 'production'
        ? [EEnvs.prod, EEnvs.uatii]?.includes?.(env.REACT_APP_ENV ?? ``)
            ? env.REACT_APP_COOKIE_DOMAIN
            : window.location.hostname
        : window.location.hostname;

const isLocalStorageSupported = () => {
    const check = () => {
        const storage = window.localStorage;
        const string4Kb = 'test'.repeat(1000);
        storage.setItem('longString', string4Kb);
        storage.removeItem('longString');
    };

    try {
        check();
        return true;
    } catch (error: unknown) {
        if (error instanceof Error && error.name === 'QuotaExceededError') {
            try {
                window.localStorage.clear();
                check();
                return true;
            } catch (error) {}
        }
        console.warn('LocalStorage is not supported');
        AlertsService.showWarning('LocalStorage is not supported');
        return false;
    }
};

const isLocalStorageAvailable = isLocalStorageSupported();

const Config = {
    maxFileSizeMb: 20,
    amplify: {
        Auth: {
            userPoolId: (env.awsconfig as any).aws_user_pools_id,
            userPoolWebClientId: (env.awsconfig as any).aws_user_pools_web_client_id,
            region: (env.awsconfig as any).aws_cognito_region,
            oauth: (env.awsconfig as any).oauth,
            federationTarget: (env.awsconfig as any).federationTarget,
            storage: ReducedCookieStorage,
            authenticationFlowType: 'CUSTOM_AUTH',
        } as any,
    },
    canLoginWithSocialMedia: !!(env.awsconfig as any).federationTarget,
    isMarketing: env.REACT_APP_IS_MARKETING,
    marketing: {
        supportLink: env.REACT_APP_SUPPORT_URL,
        baseUrl: env.REACT_APP_MSITE_URL,
    },
    application: {
        baseUrl: env.REACT_APP_APPLICATION_URL,
        name: env.REACT_APP_NAME,
        version: env.REACT_APP_VERSION,
        refreshTokenIntervalMin: 50,
        resourcesReloadIntervalMin: 60,
        checkRefreshTokenExpirationIntervalMin: 60,
        refreshTokenLifetimeBeforeLogoutDays: 28,
        cookieDomain: cookieDomain,
    },
};

export default Config;
